import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Group,
  Loader,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconInfoCircle, IconRefresh, IconSearch } from '@tabler/icons-react';
import type { NotificationLogItem } from '@expeditor/shared';
import { mailerApi } from '../../shared/api/mailer';

const STATUS_COLOR: Record<string, string> = {
  SENT: 'green',
  FAILED: 'red',
  QUEUED: 'gray',
  READ: 'blue',
};

const KIND_LABEL: Record<string, string> = {
  'verify-email': 'Подтверждение email',
  'resend-verify-email': 'Подтверждение (повтор)',
  'password-reset': 'Сброс пароля',
};

export function MailerLogPage() {
  const [items, setItems] = useState<NotificationLogItem[]>([]);
  const [status, setStatus] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await mailerApi.log({
        status: (status as 'SENT' | 'FAILED' | 'QUEUED' | 'READ' | undefined) ?? undefined,
        search: search.trim() || undefined,
        limit: 200,
      });
      setItems(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(reload, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, search]);

  const fmt = (s: string) => new Date(s).toLocaleString('ru-RU');

  return (
    <Box>
      <Group justify="space-between" mb="md">
        <Title order={3}>Журнал писем</Title>
        <Tooltip label="Обновить">
          <ActionIcon variant="default" onClick={reload}>
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>

      <Group mb="md">
        <TextInput
          placeholder="Поиск по получателю / теме / ошибке"
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          w={350}
        />
        <Select
          placeholder="Все статусы"
          clearable
          value={status}
          onChange={setStatus}
          data={[
            { value: 'SENT', label: 'Отправлено' },
            { value: 'FAILED', label: 'Ошибка' },
            { value: 'QUEUED', label: 'В очереди' },
            { value: 'READ', label: 'Прочитано' },
          ]}
          w={180}
        />
      </Group>

      {error && (
        <Alert color="red" icon={<IconInfoCircle size={16} />} mb="md">
          {error}
        </Alert>
      )}

      {loading ? (
        <Loader />
      ) : (
        <Table striped withTableBorder withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: 150 }}>Время</Table.Th>
              <Table.Th style={{ width: 110 }}>Статус</Table.Th>
              <Table.Th style={{ width: 220 }}>Получатель</Table.Th>
              <Table.Th style={{ width: 180 }}>Тип</Table.Th>
              <Table.Th>Тема</Table.Th>
              <Table.Th>Ошибка</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((i) => (
              <Table.Tr key={i.id}>
                <Table.Td>
                  <Text size="xs">{fmt(i.createdAt)}</Text>
                  {i.sentAt && i.status === 'SENT' && (
                    <Text size="xs" c="dimmed">
                      → {fmt(i.sentAt)}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Badge color={STATUS_COLOR[i.status] ?? 'gray'} variant="light">
                    {i.status}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="xs">{i.recipient ?? '—'}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">
                    {i.kind ? KIND_LABEL[i.kind] ?? i.kind : '—'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="xs">{i.subject}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c={i.error ? 'red' : 'dimmed'}>
                    {i.error ?? '—'}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
            {items.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Text c="dimmed" ta="center" py="md">
                    Журнал пуст (по текущим фильтрам)
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      )}
    </Box>
  );
}
